import { create } from 'zustand';
import { CartItem, CustomerDetails } from '@/types';
import { requestNotificationPermission } from '@/lib/firebase';
import { calculateDiscount } from '@/lib/utils';

interface StoreState {
  darkMode: boolean;
  toggleDarkMode: () => void;
  cart: CartItem[];
  addToCart: (product: CartItem) => void;
  removeFromCart: (id: number) => void;
  updateCartQuantity: (id: number, quantity: number, variations?: CartItem['selectedVariations']) => void;
  clearCart: () => void;
  wishlist: number[];
  toggleWishlist: (productId: number) => void;
  setWishlist: (productIds: number[]) => void;
  customerDetails: CustomerDetails;
  setCustomerDetails: (details: CustomerDetails) => void;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
  subscribeToNotifications: () => Promise<void>;
}

export const useStore = create<StoreState>((set) => ({
  darkMode: localStorage.getItem('darkMode') === 'true',
  toggleDarkMode: () =>
    set((state) => {
      const newDarkMode = !state.darkMode;
      localStorage.setItem('darkMode', String(newDarkMode));
      return { darkMode: newDarkMode };
    }),

  cart: JSON.parse(localStorage.getItem('cart') || '[]'),
  addToCart: (product) =>
    set((state) => {
      const existingItem = state.cart.find((item) => item.id === product.id);
      let newCart;

      if (existingItem) {
        if (product.selectedVariations) {
          // Merge variations while preserving IDs
          const updatedVariations = product.selectedVariations.map(newVar => {
            const existingVar = existingItem.selectedVariations?.find(
              ev => ev.variation === newVar.variation
            );
            if (existingVar) {
              return {
                ...newVar,
                quantity: existingVar.quantity + newVar.quantity,
                v_id: newVar.v_id // Use v_id instead of id
              };
            }
            return newVar;
          });
          
          // Add any variations that don't exist in the new item
          const existingOnlyVars = existingItem.selectedVariations?.filter(
            ev => !product.selectedVariations?.some(nv => nv.variation === ev.variation)
          ) || [];
          
          newCart = state.cart.map((item) =>
            item.id === product.id
              ? { ...item, selectedVariations: [...updatedVariations, ...existingOnlyVars] }
              : item
          );
        } else {
          newCart = state.cart.map((item) =>
            item.id === product.id
              ? { ...item, quantity: item.quantity + product.quantity }
              : item
          );
        }
      } else {
        newCart = [...state.cart, product];
      }

      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  removeFromCart: (id) =>
    set((state) => {
      const newCart = state.cart.filter((item) => item.id !== id);
      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  updateCartQuantity: (id, quantity, variations) =>
    set((state) => {
      const newCart = state.cart.map((item) => {
        if (item.id === id) {
          const updatedItem = {
            ...item,
            quantity,
            selectedVariations: variations || item.selectedVariations,
          };
          const totalQuantity = updatedItem.selectedVariations 
            ? updatedItem.selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
            : updatedItem.quantity;
          updatedItem.discountInfo = calculateDiscount(updatedItem, totalQuantity);
          return updatedItem;
        }
        return item;
      });
      localStorage.setItem('cart', JSON.stringify(newCart));
      return { cart: newCart };
    }),

  clearCart: () =>
    set(() => {
      localStorage.removeItem('cart');
      return { cart: [] };
    }),

  wishlist: JSON.parse(localStorage.getItem('wishlist') || '[]'),
  toggleWishlist: (productId) =>
    set((state) => {
      const newWishlist = state.wishlist.includes(productId)
        ? state.wishlist.filter((id) => id !== productId)
        : [...state.wishlist, productId];
      localStorage.setItem('wishlist', JSON.stringify(newWishlist));
      return { wishlist: newWishlist };
    }),

  setWishlist: (productIds) =>
    set(() => {
      localStorage.setItem('wishlist', JSON.stringify(productIds));
      return { wishlist: productIds };
    }),

  customerDetails: JSON.parse(localStorage.getItem('customerDetails') || '{}'),
  setCustomerDetails: (details) =>
    set(() => {
      localStorage.setItem('customerDetails', JSON.stringify(details));
      return { customerDetails: details };
    }),

  notificationsEnabled: localStorage.getItem('notificationsEnabled') === 'true',
  setNotificationsEnabled: (enabled) =>
    set(() => {
      localStorage.setItem('notificationsEnabled', String(enabled));
      return { notificationsEnabled: enabled };
    }),

  subscribeToNotifications: async () => {
    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/subscribe-notifications.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token, domain }),
        });

        if (!response.ok) {
          throw new Error('Failed to register notification token with server');
        }

        const data = await response.json();
        if (!data.status) {
          throw new Error(data.message || 'Failed to subscribe to notifications');
        }

        set({ notificationsEnabled: true });
        localStorage.setItem('notificationsEnabled', 'true');
      }
    } catch (error) {
      console.warn('Failed to subscribe to notifications:', error);
      set({ notificationsEnabled: false });
      localStorage.setItem('notificationsEnabled', 'false');
      throw error;
    }
  },
}));